import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getNormalizedTrafficSource } from "@/lib/analytics-source";

const ANALYTICS_VISITOR_KEY = "analytics_visitor_id";

const PUBLIC_TRACKED_PATHS = new Set([
  "/",
  "/testimonials",
  "/referral-program",
  "/auth",
  "/reset-password",
  "/chatters",
]);

const shouldTrackPath = (pathname: string) => {
  if (PUBLIC_TRACKED_PATHS.has(pathname)) return true;
  if (pathname.startsWith("/t/")) return true;
  if (pathname.startsWith("/l/")) return true;
  return false;
};

const getVisitorId = () => {
  const existing = localStorage.getItem(ANALYTICS_VISITOR_KEY);
  if (existing) return existing;
  const generated = crypto.randomUUID();
  localStorage.setItem(ANALYTICS_VISITOR_KEY, generated);
  return generated;
};

export const TrackingScript = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!shouldTrackPath(location.pathname)) return;

    const trackingKey = `analytics_visit_tracked:${location.pathname}:${location.search}`;
    if (sessionStorage.getItem(trackingKey)) return;

    const url = new URL(window.location.href);
    const utmSource = url.searchParams.get("utm_source");
    const utmMedium = url.searchParams.get("utm_medium");
    const utmCampaign = url.searchParams.get("utm_campaign");
    const utmTerm = url.searchParams.get("utm_term");
    const utmContent = url.searchParams.get("utm_content");
    const referrer = document.referrer || null;
    const normalizedSource = getNormalizedTrafficSource(utmSource, referrer, navigator.userAgent);

    if (utmSource?.trim()) {
      sessionStorage.setItem("utm_source", utmSource.trim());
    } else if (normalizedSource !== "direct") {
      sessionStorage.setItem("utm_source", normalizedSource);
    }

    const visitorId = getVisitorId();
    sessionStorage.setItem(trackingKey, "1");

    supabase.functions
      .invoke("track-analytics-visit", {
        body: {
          visitor_id: visitorId,
          page_path: `${location.pathname}${location.search}`,
          referrer,
          user_agent: navigator.userAgent,
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
          utm_term: utmTerm,
          utm_content: utmContent,
        },
      })
      .then(({ error }) => {
        if (error) {
          console.error("Failed to track analytics visit", error);
          sessionStorage.removeItem(trackingKey);
        }
      })
      .catch((error) => {
        console.error("Failed to track analytics visit", error);
        sessionStorage.removeItem(trackingKey);
      });
  }, [location.pathname, location.search]);

  return null;
};
